import { generatePath, useLocation } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { ModelType } from '../../common/enums.js';
import { omaElisaForCompaniesMsg, returnMsg, subscriptionNotCompatibleWithVakioMsg, t } from '../../common/i18n';
import { getSubscriptionBreadcrumbName, getSubscriptionUrlId } from '../../common/utils/subscriptionUtils.js';
import { NumberRange, PbxType, SubscriptionType } from '../../generated/api/models.js';
import { AttachRingPbxContent } from '../AttachPbxContent/AttachRingPbxContent';
import { BreadCrumbsWithTitle } from '../BreadCrumbsWithTitle/BreadCrumbsWithTitle.js';
import { DetailsWrapper } from '../DetailsWrapper';
import { EmptyOrError } from '../EmptyOrError';
import { Loading } from '../Loading';
import { CATEGORY_TEXTS } from '../SubscriptionDetails/subscriptionDetailsCommon.js';
import { getRingServiceLevelsFromOnlineModel } from './attachRingDataConverter.js';
import type { ServiceLevel, ServiceLevelAddon } from './ServiceLevelAndAddonsInterfaces.js';
import type { OnlineModel, Subscription } from '../../generated/api/models.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { AttachPbxContentProps } from '../AttachPbxContent/AttachRingPbxContent';
import type { JSX } from 'react';

export interface AttachRingProps extends AttachPbxContentProps {
  pbxServiceLevels?: ServiceLevel[];
  pbxServiceLevelAddons?: ServiceLevelAddon[];
  associations?: AssociationRecord[];
  dependencies?: DependencyRecord[];
  onlineModels?: OnlineModel[];
  addOnAssociations: AssociationRecord[];
  onSubmitRingOrder?: AttachPbxContentProps['onSubmitRingOrder'];
}

const getErrorElement = (
  subscription: Subscription,
  onClickSubscription: (id: string) => void,
  pbxSolutions?: Subscription[]
) => {
  const ringNotFoundError = (
    <EmptyOrError
      critical={true}
      id="vakio-not-found"
      text={t.EWI0('Your company does not have an active Ring solution.')}
      onButtonClick={() => {
        onClickSubscription(getSubscriptionUrlId(subscription));
      }}
      buttonText={t.VH59(returnMsg)}
    />
  );

  if (!pbxSolutions || pbxSolutions.length < 1) {
    return ringNotFoundError;
  }

  const [defaultPbxSolution] = pbxSolutions;
  if (
    !defaultPbxSolution.details ||
    !defaultPbxSolution.details.mobilePbx ||
    defaultPbxSolution.details.mobilePbx.numberRanges.find(
      numberRange => numberRange.rangeType === NumberRange.RangeTypeEnum.PSTN
    ) === undefined ||
    defaultPbxSolution.details.mobilePbx.numberRanges.find(
      numberRange => numberRange.rangeType === NumberRange.RangeTypeEnum.EXTENSION
    ) === undefined
  ) {
    return ringNotFoundError;
  }

  if (!subscription.compatiblePbxTypes?.includes(PbxType.MOBILE_PBX_LITE)) {
    return (
      <EmptyOrError
        id="not-vakio-compatible"
        text={t.CCR4(subscriptionNotCompatibleWithVakioMsg)}
        onButtonClick={() => {
          onClickSubscription(getSubscriptionUrlId(subscription));
        }}
        buttonText={t.VH59(returnMsg)}
      />
    );
  }

  if (subscription.details?.mobile?.pbxConfiguration?.pbxSolutionId) {
    return (
      <EmptyOrError
        id="already-vakio-connected"
        text={t.RWP3('Subscription already connected to Ring solution.')}
        onButtonClick={() => {
          onClickSubscription(getSubscriptionUrlId(subscription));
        }}
        buttonText={t.VH59(returnMsg)}
      />
    );
  }
  return undefined;
};

export const AttachRing = (props: AttachRingProps) => {
  const {
    category,
    companyInfo,
    subscription,
    numberRanges,
    onClickSubscription,
    onSubmitRingOrder,
    associations,
    dependencies,
    onlineModels,
    addOnAssociations,
  } = props;
  const { search } = useLocation();

  if (!subscription || props.companyInfo?.companyName === undefined) {
    return <Loading />;
  }

  const errorElement = getErrorElement(subscription, onClickSubscription, companyInfo?.pbxSolutions);
  if (errorElement) {
    return errorElement;
  }

  const ringServicesModel = onlineModels?.find(model => model.onlineModelCode === ModelType.Ring);

  const getPbxServiceLevelsAndAddons = () => {
    const ringSolution = companyInfo?.pbxSolutions?.find(
      pbxSolution => pbxSolution.subscriptionType === SubscriptionType.MOBILE_PBX
    );

    if (!ringSolution) {
      return {
        pbxServiceLevelAddons: [],
        pbxServiceLevels: [],
      };
    }

    const mobilePbxServiceLevelProduct = ringSolution.details?.mobilePbx?.mobilePbxServiceLevelProduct;
    const productCodes = mobilePbxServiceLevelProduct?.userServiceLevelCommercialProductCodes || [];
    const productAddons = mobilePbxServiceLevelProduct?.addOnServiceLevelSelections || [];

    const serviceLevelsAndAddons = getRingServiceLevelsFromOnlineModel(productCodes, productAddons, ringServicesModel);
    return {
      pbxServiceLevels: serviceLevelsAndAddons.pbxServiceLevels,
      pbxServiceLevelAddons: serviceLevelsAndAddons.pbxServiceLevelAddons,
    };
  };

  const { pbxServiceLevels, pbxServiceLevelAddons } =
    props.pbxServiceLevels && props.pbxServiceLevelAddons
      ? { pbxServiceLevels: props.pbxServiceLevels, pbxServiceLevelAddons: props.pbxServiceLevelAddons }
      : getPbxServiceLevelsAndAddons();

  const content: JSX.Element = (
    <AttachRingPbxContent
      category={category}
      companyInfo={companyInfo}
      subscription={subscription}
      numberRanges={numberRanges}
      onClickSubscription={onClickSubscription}
      pbxServiceLevels={pbxServiceLevels}
      pbxServiceLevelAddons={pbxServiceLevelAddons}
      onSubmitRingOrder={onSubmitRingOrder}
      associations={associations}
      dependencies={dependencies}
      ringModels={ringServicesModel ? [ringServicesModel] : []}
      addOnAssociations={addOnAssociations}
    />
  );

  const breadCrumbs: JSX.Element = (
    <BreadCrumbsWithTitle
      breadCrumbPaths={[
        { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
        { name: CATEGORY_TEXTS()[props.category], path: paths.PS_MOBILE_SUBSCRIPTIONS },
        {
          name: getSubscriptionBreadcrumbName(subscription),
          path:
            generatePath(paths.PS_MOBILE_SUBSCRIPTION, {
              subscriptionId: getSubscriptionUrlId(subscription),
            }) + search,
        },
        { name: 'Ring' },
      ]}
    />
  );

  return (
    <DetailsWrapper
      classes={['of-attach-ring']}
      detailsTop={breadCrumbs}
      id="of-attach-ring"
      heading={t.NENG('Add subscription to Ring')}
      heroPicto="sim"
    >
      {content}
    </DetailsWrapper>
  );
};
